import { ReactElement, useMemo, useState, useEffect, useRef } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface WeatherData {
  Baro_P: number | null;
  Humidity_x: number | null;
  Temp_T: number | null;
  TimeAbs_t: number;
  WindDir_A: number | null;
  Wind_v: number | null;
}

interface WeatherSource {
  end_time: number | null;
  lat: number | null;
  lon: number | null;
  start_time: number | null;
  ts: number;
  weather_data: WeatherData[];
}

interface WeatherControllerDict {
  bike_computer: WeatherSource | null;
  local_station: WeatherSource | null;
  manual: WeatherSource | null;
  probe: WeatherSource | null;
  probe_hybrid: WeatherSource | null;
  weather_api: WeatherSource | null;
  offsets_raw?: any;
  selection?: string;
}

interface WeatherTemperaturePlotProps {
  weatherControllerDict: WeatherControllerDict | null;
  selectedSource: string;
  utcOffsetSeconds?: number;
  height?: number;
}

export default function WeatherTemperaturePlot({
  weatherControllerDict,
  selectedSource,
  utcOffsetSeconds = 0,
  height = 384,
}: WeatherTemperaturePlotProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);

  // Wait for container to have valid dimensions before rendering chart
  useEffect(() => {
    if (containerRef.current) {
      const width = containerRef.current.offsetWidth;

      if (width > 0) {
        setIsReady(true);
      } else {
        // If width is 0, wait a bit and check again
        const timer = setTimeout(() => {
          if (containerRef.current && containerRef.current.offsetWidth > 0) {
            setIsReady(true);
          }
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  const { series, hasData, colors, timeRange, syntheticPoints } = useMemo(() => {
    if (!weatherControllerDict) {
      return { series: [], hasData: false, colors: [], timeRange: null, syntheticPoints: [] };
    }

    // Define color mapping for each source
    const sourceConfig = [
      { name: 'Weather Api', key: 'weather_api' as keyof WeatherControllerDict, color: '#3b82f6' }, // Blue
      { name: 'Probe', key: 'probe' as keyof WeatherControllerDict, color: '#10b981' }, // Green
      { name: 'Probe-API', key: 'probe_hybrid' as keyof WeatherControllerDict, color: '#06b6d4' }, // Cyan
      { name: 'Local Weather Station', key: 'local_station' as keyof WeatherControllerDict, color: '#f59e0b' }, // Amber
      { name: 'Manual', key: 'manual' as keyof WeatherControllerDict, color: '#8b5cf6' }, // Purple
      { name: 'Bike Computer', key: 'bike_computer' as keyof WeatherControllerDict, color: '#4b5563' }, // Slate
    ];

    const seriesData: any[] = [];
    const colorArray: string[] = [];
    let probeTimeRange: { min: number; max: number } | null = null;

    // Get temperature offsets from offsets_raw
    const offsets = weatherControllerDict.offsets_raw || {};

    // Process each source
    for (const config of sourceConfig) {
      const weatherSource = weatherControllerDict[config.key];

      // Skip if source has no data
      if (!weatherSource || !weatherSource.weather_data || weatherSource.weather_data.length === 0) {
        continue;
      }

      // Get the temperature offset for this source (default to 0 if not found)
      const sourceOffset = offsets[config.key]?.Temp_T ?? 0;

      // Filter out data points with null temperature and sort by time
      const validData = weatherSource.weather_data
        .filter((d) => d.Temp_T !== null && d.TimeAbs_t !== null)
        .sort((a, b) => a.TimeAbs_t - b.TimeAbs_t);

      if (validData.length === 0) {
        continue;
      }

      // Convert Unix timestamps to Date objects and apply temperature offset
      const data = validData.map((d) => ({
        x: (d.TimeAbs_t + utcOffsetSeconds) * 1000, // Apply UTC offset and convert to milliseconds for JavaScript Date
        y: (d.Temp_T as number) + sourceOffset, // Apply temperature offset
      }));

      // Calculate time range for probe data
      if (config.key === 'probe' && data.length > 0) {
        const twoHoursMs = 2 * 60 * 60 * 1000;
        probeTimeRange = {
          min: data[0].x - twoHoursMs,
          max: data[data.length - 1].x + twoHoursMs,
        };
      }

      // Highlight the selected source with thicker line
      const isSelected = config.name === selectedSource;
      seriesData.push({
        name: config.name,
        data: data,
        zIndex: isSelected ? 10 : 1, // Bring selected to front
      });
      colorArray.push(config.color);
    }

    // The visible window anchors on the actual test (probe) data +/- 2 h; with
    // no probe data yet, it anchors on the manual points +/- 2 h instead.
    const manualSeries = seriesData.find((s) => s.name === 'Manual');
    const syntheticManualPoints: { seriesIndex: number; dataPointIndex: number }[] = [];
    let windowRange = probeTimeRange;
    if (!windowRange && manualSeries && manualSeries.data.length > 0) {
      const twoHoursMs = 2 * 60 * 60 * 1000;
      windowRange = {
        min: manualSeries.data[0].x - twoHoursMs,
        max: manualSeries.data[manualSeries.data.length - 1].x + twoHoursMs,
      };
    }

    // Extend the Manual series flat to cover the window and every other
    // series' extent, mirroring the backend hold-first/hold-last
    // extrapolation — the flat line must span the test window even when the
    // manual point itself sits outside the visible range.
    if (manualSeries && manualSeries.data.length > 0) {
      const allX = seriesData.flatMap((s) => s.data.map((d: { x: number }) => d.x));
      if (windowRange) {
        allX.push(windowRange.min, windowRange.max);
      }
      const chartMin = Math.min(...allX);
      const chartMax = Math.max(...allX);
      const manualIndex = seriesData.indexOf(manualSeries);
      const first = manualSeries.data[0];
      const last = manualSeries.data[manualSeries.data.length - 1];
      if (chartMin < first.x) {
        manualSeries.data.unshift({ x: chartMin, y: first.y });
        syntheticManualPoints.push({ seriesIndex: manualIndex, dataPointIndex: 0 });
      }
      if (chartMax > last.x) {
        manualSeries.data.push({ x: chartMax, y: last.y });
        syntheticManualPoints.push({ seriesIndex: manualIndex, dataPointIndex: manualSeries.data.length - 1 });
      }
    }

    return {
      series: seriesData,
      hasData: seriesData.length > 0,
      colors: colorArray,
      timeRange: windowRange,
      syntheticPoints: syntheticManualPoints,
    };
  }, [weatherControllerDict, selectedSource, utcOffsetSeconds]);

  if (!hasData) {
    return (
      <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-rounded cr-bg-gray-50 cr-text-gray-500">
        No temperature data available
      </div>
    );
  }

  // Calculate stroke widths - make selected source thicker
  const strokeWidths = series.map((s) => (s.name === selectedSource ? 3 : 2));

  const options: ApexOptions = {
    chart: {
      type: 'line',
      height: 384,
      zoom: { enabled: true, allowMouseWheelZoom: false },
      toolbar: { show: true },
      animations: { enabled: false },
    },
    stroke: {
      curve: 'straight',
      width: strokeWidths,
    },
    colors: colors,
    xaxis: {
      type: 'datetime',
      ...(timeRange && {
        min: timeRange.min,
        max: timeRange.max,
      }),
      labels: {
        datetimeUTC: true,
        format: 'HH:mm',
      },
      title: {
        text: 'Time of Day',
        style: {
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151',
        },
      },
    },
    yaxis: {
      title: {
        text: 'Temperature (°C)',
        style: {
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151',
        },
      },
      labels: {
        formatter: (val: number) => val.toFixed(1),
      },
    },
    grid: {
      borderColor: '#e5e7eb',
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      fontWeight: 500,
      markers: {
        size: 6,
        strokeWidth: 2,
        // strokeColors: '#fff',
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
      onItemClick: {
        toggleDataSeries: true,
      },
      onItemHover: {
        highlightDataSeries: true,
      },
    },
    tooltip: {
      x: {
        format: 'HH:mm:ss',
      },
      y: {
        formatter: (val: number) => `${val.toFixed(2)} °C`,
      },
      shared: true,
      intersect: false,
    },
    markers: {
      size: series.map((s) => (s.name === selectedSource ? 5 : 3)),
      strokeColors: '#fff',
      strokeWidth: 2,
      // Synthetic flat-extrapolation endpoints on the Manual series carry no
      // marker so only user-entered points read as data.
      discrete: syntheticPoints.map((p) => ({
        seriesIndex: p.seriesIndex,
        dataPointIndex: p.dataPointIndex,
        size: 0,
        fillColor: '#8b5cf6',
        strokeColor: '#fff',
      })),
      hover: {
        size: 7,
      },
    },
    title: {
      text: 'Temperature',
      align: 'center',
    },
  };

  return (
    <div ref={containerRef} className="cr-rounded-xl cr-bg-clip-border cr-shadow-md cr-p-4" style={{ height: `${height}px` }}>
      {isReady ? (
        <Chart options={options} series={series} type="line" height="100%" />
      ) : (
        <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-rounded cr-bg-gray-50 cr-text-gray-500">
          Loading chart...
        </div>
      )}
    </div>
  );
}
